import type {CropRect} from '@EmailMakerReact/lib/email-maker-image-crop-geometry';
import {moveCropRect, resizeCropRectByHandle} from '@EmailMakerReact/lib/email-maker-image-crop-geometry';
import type {EmailImageCropModeState} from '@EmailMakerReact/lib/email-maker-image-crop-library';
import type {ClientPoint, CropWindowDrag, CropWindowHandleSpec, ImageCropOverlayGeometry, ImageCropOverlayRects, NaturalPoint, OverlayBox} from './image-crop-overlay.types';
import {CROP_WINDOW_HANDLE_SPECS, CropWindowDragKind} from './image-crop-overlay.types';
import styles from './image-crop-overlay.module.scss';

const CROP_HANDLE_REACH_PX = Number.parseFloat(styles.handleReachPx) || 0;

export function getImageCropOverlayGeometry(rects: ImageCropOverlayRects): ImageCropOverlayGeometry {
  return {
    frameBox: {
      left: rects.frameRect.left + rects.frameClientLeft - rects.layerRect.left - CROP_HANDLE_REACH_PX,
      top: rects.frameRect.top + rects.frameClientTop - rects.layerRect.top - CROP_HANDLE_REACH_PX,
      width: rects.frameClientWidth + 2 * CROP_HANDLE_REACH_PX,
      height: rects.frameClientHeight + 2 * CROP_HANDLE_REACH_PX,
    },
    frameClipBox: {
      left: CROP_HANDLE_REACH_PX,
      top: CROP_HANDLE_REACH_PX,
      width: rects.frameClientWidth,
      height: rects.frameClientHeight,
    },
    imageBox: {
      left: rects.imageRect.left,
      top: rects.imageRect.top,
      width: rects.imageRect.width,
      height: rects.imageRect.height,
    },
  };
}

export function isSameImageCropOverlayGeometry(first: ImageCropOverlayGeometry, second: ImageCropOverlayGeometry): boolean {
  return isSameOverlayBox(first.frameBox, second.frameBox) && isSameOverlayBox(first.frameClipBox, second.frameClipBox) && isSameOverlayBox(first.imageBox, second.imageBox);
}

export function getImageCropScale(geometry: ImageCropOverlayGeometry, cropModeState: EmailImageCropModeState): number {
  return geometry.imageBox.width / cropModeState.imageSize.width;
}

export function getCropWindowInImageBox(cropWindow: CropRect, scale: number): OverlayBox {
  return {
    left: cropWindow.x * scale,
    top: cropWindow.y * scale,
    width: cropWindow.width * scale,
    height: cropWindow.height * scale,
  };
}

export function getCropWindowInFrameBox(geometry: ImageCropOverlayGeometry, cropWindow: CropRect, scale: number): OverlayBox {
  const windowInImageBox = getCropWindowInImageBox(cropWindow, scale);
  return {
    ...windowInImageBox,
    left: geometry.frameClipBox.left + geometry.imageBox.left + windowInImageBox.left,
    top: geometry.frameClipBox.top + geometry.imageBox.top + windowInImageBox.top,
  };
}

export function getVisibleCropWindowHandleSpecs(aspectRatio: number | null): CropWindowHandleSpec[] {
  if (aspectRatio === null) {
    return CROP_WINDOW_HANDLE_SPECS;
  }
  return CROP_WINDOW_HANDLE_SPECS.filter((spec) => {
    return spec.isCorner;
  });
}

export function getDraggedCropWindow(drag: CropWindowDrag, pointer: ClientPoint, cropModeState: EmailImageCropModeState): CropRect {
  const deltaNaturalX = (pointer.x - drag.startPointer.x) / drag.scale;
  const deltaNaturalY = (pointer.y - drag.startPointer.y) / drag.scale;
  if (drag.kind === CropWindowDragKind.MOVE) {
    return moveCropRect({rect: drag.startCropWindow, deltaX: deltaNaturalX, deltaY: deltaNaturalY, imageSize: cropModeState.imageSize});
  }
  const handlePoint = getCropHandlePoint(drag.startCropWindow, drag.spec);
  return resizeCropRectByHandle({
    rect: drag.startCropWindow,
    handle: drag.spec.handle,
    pointX: handlePoint.x + deltaNaturalX,
    pointY: handlePoint.y + deltaNaturalY,
    aspectRatio: cropModeState.aspectRatio,
    imageSize: cropModeState.imageSize,
  });
}

function getCropHandlePoint(cropWindow: CropRect, spec: CropWindowHandleSpec): NaturalPoint {
  return {
    x: cropWindow.x + cropWindow.width * spec.fractionX,
    y: cropWindow.y + cropWindow.height * spec.fractionY,
  };
}

function isSameOverlayBox(first: OverlayBox, second: OverlayBox): boolean {
  return first.left === second.left && first.top === second.top && first.width === second.width && first.height === second.height;
}
